<template>
  <div>
    <v-card elevation="0">
      <v-card-title class="d-flex align-content-end flex-wrap align-items-end">
        <v-container fluid>
          <v-row>
            <v-col cols="12" lg="6">
              <v-menu
                ref="showDateDialog"
                v-model="showDateDialog"
                :close-on-content-click="false"
                :return-value.sync="query.dateRange"
                transition="scale-transition"
                offset-y
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRangeText"
                    label="ช่วงวันที่"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    hide-details
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-if="showDateDialog"
                  v-model="query.dateRange"
                  range
                  width="320"
                  :max="$moment().format('YYYY-MM-DD')"
                >
                  <v-spacer />

                  <v-btn text color="primary" @click="showDateDialog = false"> Cancel </v-btn>
                  <v-btn color="primary" @click="$refs.showDateDialog.save(query.dateRange)"> OK </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" lg="6">
              <div class="d-flex align-items-baseline my-2">
                <v-text-field
                  v-model="query.text"
                  prepend-icon="mdi-magnify"
                  autocomplete="off"
                  hide-details
                  label="ค้นหา"
                  clearable
                  class="mx-3"
                  @input="query.text = $event"
                />
                <v-btn color="primary" @click="fetchData">
                  <v-icon left> mdi-check </v-icon>
                  ค้นหา
                </v-btn>
                <v-btn color="secondary" outlined class="ml-2" @click="resetFilter"> ล้าง </v-btn>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="d-flex align-items-start flex-column flex-lg-row">
              <v-btn-toggle v-model="query.type" multiple mandatory dense class="mr-4 my-2" @change="fetchData">
                <v-btn value="debit"> <v-icon left> mdi-arrow-left </v-icon>ฝาก </v-btn>
                <v-btn value="credit"> <v-icon left> mdi-arrow-right </v-icon>ถอน </v-btn>
              </v-btn-toggle>
              <v-btn-toggle v-model="query.status" multiple mandatory dense class="my-2" @change="fetchData">
                <v-btn value="submitted"> รอ </v-btn>
                <v-btn value="approved"> ยืนยันแล้ว </v-btn>
                <v-btn value="rejected"> ปฎิเสธแล้ว </v-btn>
                <v-btn value="cancelled"> ยกเลิก </v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div>
                <span class="text-sm d-block">ธนาคารของเอเจน</span>
                <v-btn-toggle v-model="query.banks" multiple @change="fetchData">
                  <v-btn
                    v-for="bank in agentBanks"
                    :key="bank.id"
                    :title="`${bank.account_name} - ${bank.account_number}`"
                    :value="bank.account_number"
                  >
                    <v-img :src="bank.logo_url" width="25" />
                    <p class="text-xs mb-0 text-left" />
                  </v-btn>
                </v-btn-toggle>
              </div>
              <div>
                <span class="text-sm d-block">ธนาคารของยูสเซอร์</span>
                <v-btn-toggle v-model="query.userBanks" multiple @change="fetchData">
                  <v-btn
                    v-for="bank in ['bay', 'ktb', 'kbank', 'scb', 'bbl', 'tmb', 'baac', 'gsb', 'kkp', 'uob']"
                    :key="bank"
                    :value="bank"
                  >
                    <v-img :src="`/bank/${bank}.svg`" width="25" />
                    <p class="text-xs mb-0 text-left" />
                  </v-btn>
                </v-btn-toggle>
              </div>
              <div class="d-flex mr-3 flex-column flex-lg-row align-items-baseline">
                <span class="mr-2 d-inline-block text-sm mr-2">จำนวนเงิน</span>
                <div class="d-flex flex-row space-between mr-2">
                  <v-text-field
                    v-model="query.amountRange.minimum"
                    type="number"
                    minimum="0"
                    clearable
                    label="จาก"
                    prefix="฿"
                    style="max-width: 120px"
                    class="text-right text-monospace"
                    hide-details
                  />
                  <v-icon class="mx-2"> mdi-arrow-right </v-icon>
                  <v-text-field
                    v-model="query.amountRange.maximum"
                    type="number"
                    :minimum="Math.max(0, query.amountRange.minimum)"
                    clearable
                    label="ถึง"
                    prefix="฿"
                    style="max-width: 120px"
                    class="text-right text-monospace"
                    hide-details
                  />
                </div>
                <div class="d-flex flex-row py-2">
                  <v-btn color="primary" @click="fetchData">
                    <v-icon left> mdi-check </v-icon>
                    ค้นหา
                  </v-btn>
                  <v-btn color="secondary" outlined class="ml-2" @click="resetFilter"> ล้าง </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <div>
          <v-btn v-if="newRequestCount > 0" color="red" outlined @click="loadNewItem">
            มีรายการใหม่
            <v-chip right color="red text-white" x-small class="ml-2">
              {{ newRequestCount }}
            </v-chip>
          </v-btn>
        </div>
        <v-data-table
          :footer-props="footer"
          :headers="headers"
          :items.sync="items"
          :loading="loading"
          :multi-sort="false"
          :options.sync="options"
          :search.sync="search"
          :server-items-length="totalItems"
          class="elevation-0"
          item-key="id"
          :item-class="rowClass"
        >
          <!-- item -->
          <template #item.submitted_at="{ item }">
            <span class="d-block">
              {{
                $moment(item.submitted_at == null ? item.created_at : item.submitted_at).format('YYYY-MM-DD HH:mm:ss')
              }}
            </span>

            <v-chip
              pill
              class="pa-1 m-0"
              x-small
              :outlined="
                item.status == 'submitted' &&
                $moment().diff($moment(item.submitted_at == null ? item.created_at : item.submitted_at), 'seconds') >
                  300
              "
              :color="
                item.status == 'submitted' &&
                $moment().diff($moment(item.submitted_at == null ? item.created_at : item.submitted_at), 'seconds') >
                  300
                  ? 'red'
                  : 'light'
              "
            >
              <span v-if="item.status == 'submitted'">{{
                $moment(item.submitted_at == null ? item.created_at : item.submitted_at).fromNow()
              }}</span>
              <span v-else>{{
                $moment(item.submitted_at == null ? item.created_at : item.submitted_at).from(item.updated_at)
              }}</span>
            </v-chip>
          </template>
          <template #item.transactioned_at="{ item }">
            <span v-if="item.transactioned_at" class="d-block">{{
              $moment(item.transactioned_at).format('YYYY-MM-DD HH:mm:ss')
            }}</span>
          </template>
          <template #item.user="{ item }">
            <a :href="`/management/member/credit/${item.user.id}`" target="_blank">
              <span class="my-1 d-block"> {{ item.user.name }}</span></a
            >
          </template>
          <template #item.bank="{ item }">
            <div class="d-flex flex-row align-items-center mb-2">
              <v-img :src="item.user.banks[0].logo_url" max-width="25" max-height="25" />
              <p class="mb-0 ml-3 text-left" style="font-size: 1em">
                {{ item.user.banks[0].account_name }}<br />
                <span class="text-monospace">{{ item.user.banks[0].account_number }}</span>
              </p>
            </div>
          </template>
          <template #item.type="{ item }">
            <v-chip v-if="item.type == 'debit'" class="ma-2" color="success" small text-color="white">
              <v-icon left x-small text-color="white"> mdi-arrow-right </v-icon>
              ฝาก
            </v-chip>
            <v-chip v-else="item.type == 'credit'" class="ma-2" color="red" text-color="white" small>
              <v-icon left x-small text-color="white"> mdi-arrow-left </v-icon>ถอน
            </v-chip>
          </template>
          <template #item.account_name="{ item }">
            <div v-if="item.type == 'debit'" class="d-flex flex-row align-items-center mb-2">
              <v-img :src="`/bank/${item.bank}.svg`" max-height="25" max-width="25" />
              <p class="mb-0 ml-3 text-left" style="font-size: 1em">
                {{ item.account_name }}<br />
                <span class="text-monospace">{{ item.account_number }}</span>
              </p>
            </div>
          </template>

          <template #item.amount="{ item }">
            <span v-if="item.type == 'debit'" class="text-lg"
              >{{
                item.amount.toLocaleString('en-US', {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })
              }}
              <div v-if="item.status === 'submitted'" class="d-flex">
                <input v-model="decimal_status[item.id]" type="checkbox" />ปัดเศษ
              </div>
            </span>
            <span v-else="item.type == 'credit'" class="text-lg">{{ item.amount.toLocaleString('en-US') }}</span>
          </template>
          <template #item.status="{ item }">
            <v-icon v-if="item.approved_by === 1" small color="success"> mdi-auto-fix </v-icon>
            <v-chip v-if="item.status === 'submitted'" class="ma-2" label small color="warning"> รอ </v-chip>
            <v-chip v-else-if="item.status === 'cancelled'" class="ma-2" label small color="grey" text-color="white">
              ยกเลิก
            </v-chip>
            <v-chip v-else-if="item.status === 'approved'" class="ma-2" label small color="success">
              ยืนยันแล้ว
            </v-chip>
            <v-chip
              v-else="item.status === 'rejected'"
              class="ma-2"
              label
              small
              color="red darken-4"
              text-color="white"
            >
              ปฎิเสธแล้ว
            </v-chip>
          </template>

          <template #item.comments="{ item }">
            <span class="d-inline-block text-truncate" style="max-width: 150px">{{ item.remark }} </span>
          </template>

          <template #item.manage="{ item }">
            <div>
              <span>
                <v-btn icon @click="openComment(item)">
                  <v-icon :color="item.comments.length > 0 && item.comments[0].message != null ? 'success' : ''"
                    >mdi-comment-text</v-icon
                  >
                </v-btn>
              </span>
            </div>
            <div v-if="item.status === 'submitted'" class="d-flex">
              <v-btn color="primary" :txnid="item.id" status="approved" @click="updateStatus"> ยืนยัน </v-btn>
              <v-btn class="mx-3" color="red" outlined :txnid="item.id" status="rejected" @click="updateStatus">
                ปฎิเสธ
              </v-btn>
            </div>
            <div v-if="item.status === 'approved'">
              <button-hold-to-confirm color="red" label="ยกเลิกรายการ" @confirmed="cancelTransaction(item.id)" />
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="commentItem" width="500" max-width="100vw" persistent>
      <v-card>
        <v-toolbar dark color="red darken-1">
          <v-card-title>
            <span class="text-body-1">โน๊ตของเอเจน</span>
          </v-card-title>
          <v-spacer />
          <v-toolbar-items>
            <v-btn icon dark @click="closeComment()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-container class="mt-5">
          <v-form ref="commentForm">
            <v-row>
              <v-col cols="12">
                <v-textarea v-model="commentDetail" solo name="commentDetail" :value="commentDetail" />
              </v-col>
            </v-row>

            <v-btn color="success" class="mr-4" @click="addComment()"> บันทึก </v-btn>
          </v-form>
        </v-container>

        <v-divider />
        <v-card-actions>
          <v-btn color="red darken-1" text outlined @click="closeComment()"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogPreload" persistent max-width="290">
      <v-card color="red" dark>
        <v-card-text>
          กำลังประมวลผล
          <v-progress-linear indeterminate color="white" class="mb-0" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import axios from 'axios';
import moment from 'moment-timezone';
import Vue from 'vue';
import { Echo } from '../plugins/pusher';
import ButtonHoldToConfirm from './button-hold-to-confirm.vue';

export default {
  name: 'CashTable',
  components: { ButtonHoldToConfirm },
  props: {},

  setup() {
    const { play } = useSound(alertSound, {});
    return { play };
  },
  data() {
    return {
      footer: {
        itemsPerPageOptions: [10, 25, 50, 100],
      },
      loading: true,
      dialogLoading: false,
      search: '',
      headers: [
        { text: 'วันที่', value: 'submitted_at' },
        { text: 'เวลาโอน', value: 'transactioned_at' },
        {
          text: 'ชื่อผู้ใช้',
          value: 'user',
          cellClass: 'text-uppercase',
          sortable: false,
        },
        {
          text: 'บัญชีผู้ใช้',
          value: 'bank',
          align: 'center',
          cellClass: 'text-uppercase',
          sortable: false,
        },
        {
          text: 'รายการ',
          value: 'type',
          align: 'center',
          sortable: false,
        },
        {
          text: 'บัญชี',
          value: 'account_name',
          align: 'center',
          justify: 'center',
          cellClass: 'text-monospace text-center',
        },
        {
          text: 'ยอด',
          value: 'amount',
          align: 'right',
          cellClass: 'text-right',
        },
        { text: 'สถานะ', value: 'status', align: 'center' },
        {
          text: 'หมายเหตุ',
          value: 'comments',
          align: 'center',
          cellClass: 'text-monospace text-center',
          sortable: false,
        },
        {
          text: 'จัดการ',
          value: 'manage',
          align: 'center',
          cellClass: 'text-center',
          sortable: false,
        },
      ],
      items: [],
      totalItems: 0,
      sortBy: null,
      sortDirection: null,
      options: {},
      query: {
        type: ['debit', 'credit'],
        status: ['submitted', 'approved'],
        text: '',
        dateRange: [moment().add(-1, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
        amountRange: {
          minimum: null,
          maximum: null,
        },
        banks: [],
        userBanks: [],
      },
      agentBanks: [],
      lockedItems: [],
      showDateDialog: false,
      newRequestCount: 0,
      selectedItem: null,
      bankList: [
        { state: 'ธนาคารกรุงศรีอยุธยา', type: 'bay' },
        { state: 'ธนาคารกรุงไทย', type: 'ktb' },
        { state: 'ธนาคารกสิกรไทย', type: 'kbank' },
        { state: 'ธนาคารไทยพาณิชย์', type: 'scb' },
        { state: 'ธนาคารกรุงเทพ', type: 'bbl' },
        { state: 'ธนาคารทีเอ็มบีธนชาต', type: 'tmb' },
        { state: 'ธนาคารธกส.', type: 'baac' },
        { state: 'ธนาคารออมสิน', type: 'gsb' },
        { state: 'ธนาคารเกียรตินาคิน', type: 'kkp' },
        { state: 'ธนาคารยูโอบี', type: 'uob' },
      ],
      decimal_status: [],
      submitCash: true,
      dialogPreload: null,
      commentItem: false,
      commentDetail: null,
    };
  },
  computed: {
    dateRangeText() {
      return this.query.dateRange.join(' ~ ');
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
  },

  mounted() {
    // get agent bank
    axios.get('/api/user/banks').then((response) => {
      this.agentBanks = response.data;
    });

    Echo.join(`agent.${window.appState.user.agent_id}`).listen('cash-request', this.recieveNewRequest);
  },

  methods: {
    fetchData() {
      this.loading = true;

      axios
        .get('/api/agent/cash', {
          params: {
            page: this.options.page,
            sortBy: this.options.sortBy[0],
            sortDirection: this.options.sortDesc.length > 0 ? (this.options.sortDesc[0] ? 'ASC' : 'DESC') : null,
            pageSize: this.options.itemsPerPage,
            start: this.query.dateRange[0],
            end: this.query.dateRange[this.query.dateRange.length - 1],
            type: this.query.type.join(','),
            status: this.query.status.join(','),
            text: this.query.text.trim().length === 0 ? null : this.query.text.trim(),
            minimum: parseFloat(this.query.amountRange.minimum) > 0 ? parseFloat(this.query.amountRange.minimum) : null,
            maximum: parseFloat(this.query.amountRange.maximum) > 0 ? parseFloat(this.query.amountRange.maximum) : null,
            banks: this.query.banks ? this.query.banks : null,
            userBanks: this.query.userBanks,
          },
        })
        .then((response) => {
          this.items = response.data.data;
          this.totalItems = response.data.total;
        })
        .catch((e) => console.error(e))
        .finally(() => {
          this.loading = false;
        });
    },

    resetFilter() {
      this.query = {
        dateRange: [moment().add(-1, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
        text: '',
        type: ['debit', 'credit'],
        status: ['submitted', 'approved'],
        sortBy: null,
        amountRange: {
          minimum: null,
          maximum: null,
        },
      };
      this.fetchData();
    },

    loadNewItem() {
      this.options.page = 1;
      this.query.status = ['submitted', 'approved'];
      this.fetchData();
      this.newRequestCount = 0;
      // EventBus.$emit("cash-request-ack");
      this.$store.commit('notification/ack', { type: 'cash-request' });
    },

    recieveNewRequest(data) {
      this.newRequestCount += 1;
    },

    rowClass(item) {
      return item.is_ambiguous ? 'red lighten-5' : '';
    },

    updateStatus(event) {
      if (this.submitCash) {
        this.submitCash = false;
        this.dialogPreload = true;
        const cashId = event.currentTarget.attributes.txnid.value;
        axios
          .create({
            headers: { 'Cache-Control': 'no-store' },
          })
          .post(`/api/agent/cash/${cashId}`, {
            decimal_status: this.decimal_status[cashId],
            agent_comment: '',
            status: event.currentTarget.attributes.status.value,
          })
          .then((response) => {
            this.dialogPreload = false;
            this.submitCash = true;
            const cashIndex = (element) => element.id == cashId;
            const cashIndexFound = this.items.findIndex(cashIndex);
            this.items[cashIndexFound].status = response.data.status;
          })
          .catch((error) => {
            Vue.$toast.open({
              type: 'error',
              message: error.response.data.message,
              duration: 5000,
              position: 'top-right',
            });
            this.dialogPreload = false;
            this.submitCash = true;
          })
          .finally(() => {
            this.dialogPreload = false;
            this.decimal_status[cashId] = false;
          });
      }
    },
    cancelTransaction(cashId) {
      this.dialogPreload = true;
      axios
        .delete(`/api/agent/cash/${cashId}`, {
          data: {
            agent_comment: '',
          },
        })
        .then((response) => {
          this.dialogPreload = false;
          // this.fetchData();
          const cashIndex = (element) => element.id == cashId;
          const cashIndexFound = this.items.findIndex(cashIndex);
          this.items[cashIndexFound].status = response.data.status;
        })
        .catch((error) => {
          Vue.$toast.open({
            type: 'error',
            message: error.response.data.message,
            duration: 5000,
            position: 'top-right',
          });
          this.dialogPreload = false;
        })
        .finally(() => {
          this.dialogPreload = false;
        });
    },

    openComment(item) {
      this.commentItem = true;
      this.selectedItem = item;
      if (item.comments.length > 0) {
        this.commentDetail = item.comments[0].message;
      }
    },
    closeComment() {
      this.fetchData();
      this.commentItem = false;
      this.$refs.commentForm.reset();
    },

    addComment() {
      if (this.commentDetail) {
        this.preload = true;
        axios
          .post(`/api/agent/cash/comment/${this.selectedItem.id}`, {
            agent_comment: this.commentDetail,
          })
          .then((response) => {
            // self.getRemark(self.userId);
          })
          .catch((e) => console.error(e.response.data.message))
          .finally(() => {
            this.preload = false;
          });
      } else {
        alert('กรุณาระบุหมายเหตุ');
      }
    },
  },
};
</script>
